import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { openCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { TranslateService } from '@ngx-translate/core';
import { SLIDE_VALIDATION_FIELDS } from '../../../config/constants';
import { RegisterUserFormService } from '../../../services';
import { ValidationResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FormValidationService {
  private _translateService = inject(TranslateService);
  private _registerUserFormService = inject(RegisterUserFormService);

  validateSlide(slideIndex: number, form: FormGroup, showErrors = false): ValidationResult {
    const result = this._getSlideValidation(slideIndex, form);
    
    if (!result.isValid && showErrors && result.errorMessage) {
      this._showErrorSnackbar(result.errorMessage);
      this._markSlideFieldsAsTouched(slideIndex, form);
    }
    
    return result;
  }

  private _getSlideValidation(slideIndex: number, form: FormGroup): ValidationResult {
    switch(slideIndex) {
      case 0:
        return this._validateDatesSlide(form);
      case 1:
        return this._validateLocationSlide(form);
      case 2:
        return this._validateTripTypeSlide(form);
      case 3:
        return this._validateInterestsSlide(form);
      case 4:
        return this._validateTransportSlide(form);
      default:
        return { isValid: false, errorMessage: 'Unknown slide' };
    }
  }

  private _validateDatesSlide(form: FormGroup): ValidationResult {
    const startDateControl = form.get('startDate');
    const endDateControl = form.get('endDate');
    const startDate = this._registerUserFormService.startDate();
    const endDate = this._registerUserFormService.endDate();
    
    const isValid = Boolean(
      startDateControl?.valid && 
      endDateControl?.valid && 
      startDate && 
      endDate
    );
    
    return {
      isValid,
      errorMessage: !isValid ? 'Selecciona las fechas de inicio y fin del viaje' : undefined
    };
  }

  private _validateLocationSlide(form: FormGroup): ValidationResult {
    const locationControl = form.get('startLocationAddress');
    const startLocation = this._registerUserFormService.startLocation();
    
    const hasControlValue = Boolean(locationControl?.value?.trim());
    const hasServiceLocation = Boolean(startLocation?.address?.trim());
    const isControlValid = Boolean(locationControl?.valid);
    
    const isValid = hasControlValue && hasServiceLocation && isControlValid;
    
    return {
      isValid,
      errorMessage: !isValid ? 'Selecciona un punto de inicio válido' : undefined
    };
  }

  private _validateTripTypeSlide(form: FormGroup): ValidationResult {
    const tripTypeControl = form.get('tripType');
    const tripTypeId = this._registerUserFormService.tripTypeId();
    
    const isValid = Boolean(tripTypeControl?.valid && tripTypeId);
    
    return {
      isValid,
      errorMessage: !isValid ? 'Selecciona un tipo de viajero' : undefined
    };
  }

  private _validateInterestsSlide(form: FormGroup): ValidationResult {
    const interestsControl = form.get('interestIdList');
    const interestIdList = this._registerUserFormService.interests();
    
    const isValid = Boolean(
      interestsControl?.valid && 
      interestIdList?.length > 0
    );
    
    return {
      isValid,
      errorMessage: !isValid ? 'Selecciona al menos tres intereses' : undefined
    };
  }

  private _validateTransportSlide(form: FormGroup): ValidationResult {
    const transportMeansControl = form.get('transportMeans');
    const maxDistanceControl = form.get('maxDistanceWalking');
    const transportMeans = this._registerUserFormService.transportMeans();
    const maxDistanceWalking = this._registerUserFormService.maxDistanceWalking();
    
    const isValid = Boolean(
      transportMeansControl?.valid && 
      maxDistanceControl?.valid && 
      transportMeans?.length > 0 &&
      maxDistanceWalking !== null && 
      maxDistanceWalking !== undefined
    );
    
    return {
      isValid,
      errorMessage: !isValid ? 'Selecciona al menos un medio de transporte' : undefined
    };
  }

  private _markSlideFieldsAsTouched(slideIndex: number, form: FormGroup): void {
    const fields = SLIDE_VALIDATION_FIELDS[slideIndex];
    
    if (fields) {
      fields.forEach(fieldName => {
        const control = form.get(fieldName);
        if (control) {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });
    }
  }

  private _showErrorSnackbar(message: string): void {
    openCiSnackbar({
      content: this._translateService.instant(message),
      status: 'error',
      'icon-name': 'error',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
    });
  }
}
